import { z } from "zod";

const nonEmpty = (limit: number) => z.string().min(1).max(limit)
  .refine(value => value === value.trim(), "字段不能包含首尾空白")
  .refine(value => [...value].every(character => {
    const code = character.codePointAt(0) ?? 0;
    return code >= 0x20;
  }), "字段不能包含控制字符");
const timestamp = z.string().datetime({ offset: true });
const currency = z.string().regex(/^[A-Z]{3}$/u);
const canonicalSignedDecimal = z.string().regex(/^-?(0|[1-9]\d{0,38})$/u);
const canonicalNonNegativeDecimal = z.string().regex(/^(0|[1-9]\d{0,38})$/u);
const safeId = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,255}$/u);
const safeLabel = nonEmpty(500)
  .refine(value => !/[0-9]{7,}/u.test(value), "展示字段不能包含长数字序列")
  .refine(value => ![
    "credential", "token", "password", "email", "phone", "buyer", "customer", "address",
    "正文", "电话", "邮箱", "买家", "客户", "地址",
  ].some(fragment => value.toLowerCase().includes(fragment)), "展示字段不能包含敏感资料");

export const storeManagementMissingReasonSchema = z.enum([
  "provider_field_unsupported",
  "not_collected",
  "total_unknown",
  "component_missing",
  "not_applicable",
]);
const missingReason = z.union([z.literal(""), storeManagementMissingReasonSchema]);
const sourceRevision = safeId;
const metricUnitSchema = z.enum(["minor", "count", "basis_points"]);

const metricBaseShape = {
  value: canonicalSignedDecimal.nullable(),
  unit: metricUnitSchema,
  currency: currency.nullable(),
  comparison_period: safeLabel.nullable(),
  attribution_window: safeLabel.nullable(),
  source_revision: sourceRevision,
  missing_reason_code: missingReason,
};
function refineMetric(metric: {
  value: string | null;
  unit: "minor" | "count" | "basis_points";
  currency: string | null;
  missing_reason_code: "" | z.infer<typeof storeManagementMissingReasonSchema>;
}, ctx: z.RefinementCtx, maximumBasisPoints: bigint | null = 10_000n) {
  if ((metric.value === null) !== (metric.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "指标未知值必须且只能携带缺失原因", path: ["missing_reason_code"] });
  if ((metric.unit === "minor") !== (metric.currency !== null)) ctx.addIssue({ code: "custom", message: "只有最小货币单位指标必须携带三位币种", path: ["currency"] });
  if (metric.value !== null && metric.unit !== "minor" && metric.value.startsWith("-")) ctx.addIssue({ code: "custom", message: "计数与基点不能为负数", path: ["value"] });
  if (metric.value !== null && metric.unit === "basis_points" && maximumBasisPoints !== null && BigInt(metric.value) > maximumBasisPoints) ctx.addIssue({ code: "custom", message: "基点不能超过 10000", path: ["value"] });
}
export const storeManagementMetricSchema = z.object({ key: nonEmpty(128), ...metricBaseShape }).strict().superRefine(refineMetric);

function metricListSchema<const Keys extends readonly [string, ...string[]]>(keys: Keys) {
  const keySchema = z.enum(keys);
  const metricSchema = z.object({ key: keySchema, ...metricBaseShape }).strict().superRefine(refineMetric);
  return z.object({
    metrics: z.array(metricSchema).max(keys.length),
  }).strict().superRefine((data, ctx) => {
    const found = data.metrics.map(metric => metric.key);
    if (new Set(found).size !== found.length) ctx.addIssue({ code: "custom", message: "指标 key 不能重复", path: ["metrics"] });
  });
}

export const storeManagementCoverageDataSchema = z.object({
  covered: canonicalNonNegativeDecimal,
  total: canonicalNonNegativeDecimal.nullable(),
  total_known: z.boolean(),
  missing_reason_code: z.union([z.literal(""), z.literal("total_unknown")]),
}).strict().superRefine((coverage, ctx) => {
  if (coverage.total_known) {
    if (coverage.total === null || coverage.missing_reason_code !== "") ctx.addIssue({ code: "custom", message: "已知总量必须给出总数且不能携带缺失原因" });
    else if (BigInt(coverage.covered) > BigInt(coverage.total)) ctx.addIssue({ code: "custom", message: "覆盖数不能大于总数", path: ["covered"] });
  } else if (coverage.total !== null || coverage.missing_reason_code !== "total_unknown") {
    ctx.addIssue({ code: "custom", message: "未知总量必须为空并明确 total_unknown" });
  }
});

const sectionStateShape = {
  key: nonEmpty(128),
  state: z.enum(["available", "missing", "unavailable"]),
  missing_reason: z.string().max(500),
};
function sectionSchema<const Kind extends string, Schema extends z.ZodType>(kind: Kind, data: Schema) {
  return z.object({ ...sectionStateShape, kind: z.literal(kind), data: data.nullable() }).strict();
}

const storePerformanceKeys = ["gross_sales_minor", "orders", "units_sold", "visitors", "conversion_bps"] as const;
const inventorySummaryKeys = ["on_hand", "available", "reserved", "inbound", "low_stock_skus"] as const;
const fulfillmentStatuses = ["pending_dispatch", "shipped", "in_transit", "delayed", "exception"] as const;
const fulfillmentStatusSchema = z.enum(fulfillmentStatuses);
const afterSalesKeys = ["refunds", "returns", "disputes", "reviews"] as const;
const afterSalesMetricSchema = z.object({
  key: z.enum(afterSalesKeys),
  ...metricBaseShape,
  unit: z.literal("count"),
  currency: z.null(),
}).strict().superRefine(refineMetric);
const afterSalesSummaryDataSchema = z.object({
  metrics: z.array(afterSalesMetricSchema).length(afterSalesKeys.length),
}).strict().superRefine((data, ctx) => {
  data.metrics.forEach((metric, index) => {
    if (metric.key !== afterSalesKeys[index]) ctx.addIssue({ code: "custom", message: "售后摘要必须按固定四项顺序返回", path: ["metrics", index, "key"] });
  });
});
const adPerformanceKeys = ["spend_minor", "ending_balance_minor", "impressions", "reach", "clicks", "conversions", "revenue_minor", "roas_bps"] as const;
const adPerformanceUnits = {
  spend_minor: "minor",
  ending_balance_minor: "minor",
  impressions: "count",
  reach: "count",
  clicks: "count",
  conversions: "count",
  revenue_minor: "minor",
  roas_bps: "basis_points",
} as const;
const adPerformanceMetricSchema = z.object({ key: z.enum(adPerformanceKeys), ...metricBaseShape }).strict().superRefine((metric, ctx) => {
  // ROAS 用 10000 基点表示 1 倍，业务上可合法超过 1 倍；其他百分比指标仍保持 10000 上限。
  refineMetric(metric, ctx, metric.key === "roas_bps" ? null : 10_000n);
  if (metric.unit !== adPerformanceUnits[metric.key]) ctx.addIssue({ code: "custom", message: "广告指标与单位不匹配", path: ["unit"] });
  // 花费与归因收入是窗口累计量，不能为负；账户余额可能透支，保留 signed decimal。
  if (metric.value !== null && ["spend_minor", "revenue_minor"].includes(metric.key) && metric.value.startsWith("-")) {
    ctx.addIssue({ code: "custom", message: "广告花费与归因收入不能为负数", path: ["value"] });
  }
});
const adPerformanceMetricListSchema = z.object({
  metrics: z.array(adPerformanceMetricSchema).max(adPerformanceKeys.length),
}).strict().superRefine((data, ctx) => {
  const keys = data.metrics.map(metric => metric.key);
  if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", message: "广告指标 key 不能重复", path: ["metrics"] });
});
const socialPerformanceKeys = ["published_count", "engagement_count"] as const;
const socialPerformanceMetricSchema = z.object({ key: z.enum(socialPerformanceKeys), ...metricBaseShape }).strict().superRefine((metric, ctx) => {
  refineMetric(metric, ctx);
  if (metric.unit !== "count" || metric.currency !== null) ctx.addIssue({ code: "custom", message: "社媒指标必须使用 count 且不带币种" });
});
const socialPerformanceMetricListSchema = z.object({
  metrics: z.array(socialPerformanceMetricSchema).max(socialPerformanceKeys.length),
}).strict().superRefine((data, ctx) => {
  const keys = data.metrics.map(metric => metric.key);
  if (new Set(keys).size !== keys.length) ctx.addIssue({ code: "custom", message: "社媒指标 key 不能重复", path: ["metrics"] });
});
const connectionStateSchema = z.enum(["healthy", "degraded", "reauthorization_required"]);

const productRankingItemSchema = z.object({
  product_id: safeId,
  label: safeLabel,
  gross_sales_minor: canonicalNonNegativeDecimal.nullable(),
  currency,
  orders: canonicalNonNegativeDecimal.nullable(),
  units_sold: canonicalNonNegativeDecimal.nullable(),
  source_revision: sourceRevision,
  missing_reason_code: missingReason,
}).strict().superRefine((item, ctx) => {
  const hasUnknown = item.gross_sales_minor === null || item.orders === null || item.units_sold === null;
  if (hasUnknown !== (item.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "商品排行未知字段必须且只能携带缺失原因", path: ["missing_reason_code"] });
});

const lowStockItemSchema = z.object({
  product_id: safeId,
  sku_id: safeId,
  label: safeLabel,
  available: canonicalNonNegativeDecimal.nullable(),
  reorder_point: canonicalNonNegativeDecimal.nullable(),
  source_revision: sourceRevision,
  missing_reason_code: missingReason,
}).strict().superRefine((item, ctx) => {
  const hasUnknown = item.available === null || item.reorder_point === null;
  if (hasUnknown !== (item.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "低库存未知字段必须且只能携带缺失原因", path: ["missing_reason_code"] });
});

const productFindingSchema = z.object({
  finding_id: safeId,
  product_id: safeId,
  severity: z.enum(["info", "warning", "critical"]),
  impact_value: canonicalSignedDecimal.nullable(),
  impact_unit: metricUnitSchema,
  currency: currency.nullable(),
  source_revision: sourceRevision,
  missing_reason_code: missingReason,
}).strict().superRefine((finding, ctx) => {
  if ((finding.impact_value === null) !== (finding.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "诊断影响未知值必须且只能携带缺失原因", path: ["missing_reason_code"] });
  const needsCurrency = finding.impact_unit === "minor" && finding.impact_value !== null;
  if (needsCurrency !== (finding.currency !== null)) ctx.addIssue({ code: "custom", message: "已知诊断货币影响必须且只能携带三位币种", path: ["currency"] });
  if (finding.impact_value !== null && finding.impact_unit !== "minor" && finding.impact_value.startsWith("-")) ctx.addIssue({ code: "custom", message: "诊断计数与基点不能为负数", path: ["impact_value"] });
});

const adCampaignSchema = z.object({
  campaign_id: safeId,
  label: safeLabel,
  status: z.enum(["enabled", "paused", "archived", "unknown"]),
  budget_minor: canonicalNonNegativeDecimal.nullable(),
  currency: currency.nullable(),
  source_revision: sourceRevision,
  missing_reason_code: missingReason,
}).strict().superRefine((campaign, ctx) => {
  if ((campaign.budget_minor === null) !== (campaign.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "广告预算未知值必须且只能携带缺失原因", path: ["missing_reason_code"] });
  if ((campaign.budget_minor !== null) !== (campaign.currency !== null)) ctx.addIssue({ code: "custom", message: "已知广告预算必须携带三位币种", path: ["currency"] });
});

export const profitComponentKeys = ["revenue", "refunds", "platform_fees", "product_costs", "advertising", "logistics", "taxes"] as const;
const profitComponentKeySchema = z.enum(profitComponentKeys);
export const profitComponentsDataSchema = z.object({
  items: z.array(z.object({
    key: profitComponentKeySchema,
    amount_minor: canonicalNonNegativeDecimal.nullable(),
    currency: currency.nullable(),
    source_revision: sourceRevision,
    missing_reason_code: missingReason,
  }).strict().superRefine((component, ctx) => {
    if ((component.amount_minor === null) !== (component.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "利润组成未知值必须且只能携带缺失原因", path: ["missing_reason_code"] });
    if (component.amount_minor !== null && component.currency === null) ctx.addIssue({ code: "custom", message: "已知利润组成必须携带三位币种", path: ["currency"] });
  })).length(profitComponentKeys.length),
}).strict().superRefine((data, ctx) => {
  data.items.forEach((item, index) => {
    if (item.key !== profitComponentKeys[index]) ctx.addIssue({ code: "custom", message: "利润组成必须按固定七项顺序返回", path: ["items", index, "key"] });
  });
  const currencies = new Set(data.items.flatMap(item => item.currency === null ? [] : [item.currency]));
  if (currencies.size > 1) ctx.addIssue({ code: "custom", message: "利润组成的已知币种必须一致", path: ["items"] });
});

export const profitResultDataSchema = z.object({
  formula_version: z.literal("merchant_profit.v1"),
  currency: currency.nullable(),
  net_profit_minor: canonicalSignedDecimal.nullable(),
  complete: z.boolean(),
  missing_component_keys: z.array(profitComponentKeySchema).max(profitComponentKeys.length),
}).strict().superRefine((result, ctx) => {
  const positions = result.missing_component_keys.map(key => profitComponentKeys.indexOf(key));
  if (new Set(result.missing_component_keys).size !== result.missing_component_keys.length || positions.some((position, index) => index > 0 && position <= positions[index - 1]!)) ctx.addIssue({ code: "custom", message: "缺失利润组成必须唯一并按固定顺序返回", path: ["missing_component_keys"] });
  if (result.complete) {
    if (result.currency === null || result.net_profit_minor === null || result.missing_component_keys.length !== 0) ctx.addIssue({ code: "custom", message: "完整利润必须携带币种、净利润且没有缺失组成" });
  } else if (result.net_profit_minor !== null || result.missing_component_keys.length === 0) {
    ctx.addIssue({ code: "custom", message: "部分利润不得计算净利润且必须列出缺失组成" });
  }
});

export const storeManagementSectionSchemas = [
  sectionSchema("store_metrics", metricListSchema(storePerformanceKeys)),
  sectionSchema("store_comparison", metricListSchema(storePerformanceKeys)),
  sectionSchema("connection_health", z.object({ provider: safeLabel, state: connectionStateSchema, last_synced_at: timestamp }).strict()),
  sectionSchema("product_ranking", z.object({ items: z.array(productRankingItemSchema).max(50) }).strict()),
  sectionSchema("product_trend", metricListSchema(storePerformanceKeys)),
  sectionSchema("source_coverage", storeManagementCoverageDataSchema),
  sectionSchema("product_findings", z.object({ items: z.array(productFindingSchema).max(100) }).strict()),
  sectionSchema("safe_evidence", z.object({ items: z.array(z.object({ evidence_id: safeId, source_kind: z.enum(["provider_metric", "provider_catalog", "analysis_artifact"]), source_id: safeId, source_revision: sourceRevision }).strict()).max(100) }).strict()),
  sectionSchema("ad_accounts", z.object({ items: z.array(z.object({
    ad_account_binding_id: safeId, display_label: safeLabel, provider: safeLabel,
    review_status: z.enum(["approved", "pending", "rejected", "unknown"]),
    billing_status: z.enum(["ready", "billing_required", "unknown"]), source_revision: sourceRevision,
  }).strict()).max(100) }).strict()),
  sectionSchema("ad_campaigns", z.object({ items: z.array(adCampaignSchema).max(100) }).strict()),
  sectionSchema("ad_metrics", adPerformanceMetricListSchema),
  sectionSchema("ad_trend", adPerformanceMetricListSchema),
  sectionSchema("ad_attribution", z.object({ window: safeLabel.nullable(), source: z.enum(["external_billing_snapshot", "provider_report"]), missing_reason_code: missingReason }).strict().superRefine((data, ctx) => {
    if ((data.window === null) !== (data.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "归因窗口未知时必须且只能携带缺失原因", path: ["missing_reason_code"] });
  })),
  sectionSchema("inventory_summary", metricListSchema(inventorySummaryKeys)),
  sectionSchema("low_stock_items", z.object({ items: z.array(lowStockItemSchema).max(100) }).strict()),
  sectionSchema("fulfillment_summary", metricListSchema(fulfillmentStatuses)),
  sectionSchema("fulfillment_exceptions", z.object({ items: z.array(z.object({
    package_id: safeId, status: fulfillmentStatusSchema, occurred_at: timestamp, source_revision: sourceRevision, missing_reason_code: z.literal(""),
  }).strict()).max(100) }).strict()),
  sectionSchema("status_mapping", z.object({ items: z.array(z.object({ source_status: safeLabel, normalized_status: fulfillmentStatusSchema }).strict()).max(100) }).strict()),
  sectionSchema("after_sales_summary", afterSalesSummaryDataSchema),
  sectionSchema("after_sales_aging", z.object({ buckets: z.array(z.object({ key: z.enum(["under_24h", "one_to_three_days", "over_three_days"]), count: canonicalNonNegativeDecimal, source_revision: sourceRevision }).strict()).length(3) }).strict().superRefine((data, ctx) => {
    const keys = ["under_24h", "one_to_three_days", "over_three_days"] as const;
    data.buckets.forEach((bucket, index) => {
      if (bucket.key !== keys[index]) ctx.addIssue({ code: "custom", message: "售后时效桶必须按固定顺序返回", path: ["buckets", index, "key"] });
    });
  })),
  sectionSchema("safe_todos", z.object({ items: z.array(z.object({
    todo_id: safeId, kind: z.enum(["refund", "return", "dispute", "review"]), priority: z.enum(["normal", "high"]), status: z.enum(["open", "in_progress"]), source_revision: sourceRevision,
  }).strict()).max(100) }).strict()),
  sectionSchema("profit_components", profitComponentsDataSchema),
  sectionSchema("profit_result", profitResultDataSchema),
  sectionSchema("settlement_status", z.object({
    state: z.enum(["pending", "settled", "partial", "unknown"]), amount_minor: canonicalNonNegativeDecimal.nullable(), currency: currency.nullable(), settled_at: timestamp.nullable(), source_revision: sourceRevision, missing_reason_code: missingReason,
  }).strict().superRefine((settlement, ctx) => {
    if ((settlement.amount_minor === null) !== (settlement.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "结算金额未知时必须且只能携带缺失原因", path: ["missing_reason_code"] });
    if ((settlement.amount_minor !== null) !== (settlement.currency !== null)) ctx.addIssue({ code: "custom", message: "已知结算金额必须携带三位币种", path: ["currency"] });
    if ((settlement.state === "settled") !== (settlement.settled_at !== null)) ctx.addIssue({ code: "custom", message: "只有已结算状态必须携带结算时间", path: ["settled_at"] });
    if (settlement.state === "settled" && settlement.amount_minor === null) ctx.addIssue({ code: "custom", message: "已结算状态必须携带金额与币种", path: ["amount_minor"] });
  })),
  sectionSchema("social_accounts", z.object({ items: z.array(z.object({ connection_id: safeId, provider: safeLabel, state: connectionStateSchema, source_revision: sourceRevision }).strict()).max(100) }).strict()),
  sectionSchema("social_queue", z.object({
    scheduled_count: canonicalNonNegativeDecimal.nullable(),
    pending_approval_count: canonicalNonNegativeDecimal.nullable(),
    failed_count: canonicalNonNegativeDecimal.nullable(),
    source_revision: sourceRevision,
    missing_reason_code: missingReason,
  }).strict().superRefine((queue, ctx) => {
    const hasUnknown = queue.scheduled_count === null || queue.pending_approval_count === null || queue.failed_count === null;
    if (hasUnknown !== (queue.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "社媒队列未知字段必须且只能携带缺失原因", path: ["missing_reason_code"] });
  })),
  sectionSchema("social_performance", socialPerformanceMetricListSchema),
  sectionSchema("email_accounts", z.object({ items: z.array(z.object({ connection_id: safeId, provider: safeLabel, state: connectionStateSchema, source_revision: sourceRevision }).strict()).max(100) }).strict()),
  sectionSchema("email_inbox", z.object({
    unread_count: canonicalNonNegativeDecimal,
    pending_count: canonicalNonNegativeDecimal.nullable(),
    overdue_count: canonicalNonNegativeDecimal.nullable(),
    source_revision: sourceRevision,
    missing_reason_code: missingReason,
  }).strict().superRefine((inbox, ctx) => {
    const hasUnknown = inbox.pending_count === null || inbox.overdue_count === null;
    if (hasUnknown !== (inbox.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "邮件待处理未知字段必须且只能携带缺失原因", path: ["missing_reason_code"] });
  })),
  sectionSchema("email_sync", z.object({ state: connectionStateSchema, last_synced_at: timestamp.nullable(), source_revision: sourceRevision, missing_reason_code: missingReason }).strict().superRefine((sync, ctx) => {
    if ((sync.last_synced_at === null) !== (sync.missing_reason_code !== "")) ctx.addIssue({ code: "custom", message: "邮件同步时间未知时必须且只能携带缺失原因", path: ["missing_reason_code"] });
  })),
] as const;

export const storeManagementSectionKindSchema = z.enum(storeManagementSectionSchemas.map(schema => schema.shape.kind.value) as [
  (typeof storeManagementSectionSchemas)[number]["shape"]["kind"]["value"],
  ...(typeof storeManagementSectionSchemas)[number]["shape"]["kind"]["value"][],
]);

export type StoreManagementSection = z.infer<(typeof storeManagementSectionSchemas)[number]>;

export function isStoreManagementSection(section: { kind: string }): section is StoreManagementSection {
  return storeManagementSectionKindSchema.safeParse(section.kind).success;
}
